import React from 'react';
import { compose } from 'recompose';
import { withUserProfile } from '../../../../../../../userProfile/userProfileContext';
import DataGrid from '@dataGrid';
import StatusBar from '@dataGrid/components/StatusBar';
import { PICKUP_STATUS } from '../../../constants';
import PickupsGridConfig from './GridConfig';
import { filterPickup } from '../utils';
import { getPickupTypes, getSpecialNeedsOptionsWithServiceLevel } from '../../../../../utils';
import { withAppConfig } from '../../../../../../../appConfigContext';
import { inject, observer } from 'mobx-react';
import GridHeader from './GridHeader';
import { csvFormulaInjectionSanitizer } from '../../../../../../../utilities/csvUtils';
import { PAST_PICKUP_GRID_FILTER_LIST } from '../Constants';
import { getDriverStopDetailsForPastPickup } from '../../../ProDetails/PickupProInfo/utils';
import { getPickupShipmentList } from '../../PickupInfoPopup/utils';
import appConstants from '../../../../../../../appConstants';
import withRouter from '../../../../../../../routes/withRouter';

@inject('DispatchStore')
@observer
class PickupsGrid extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new PickupsGridConfig({
      userConfig: this.props.userData.userProfile?.columnPref,
      showStaticRoute: this.props.configJSON?.showStaticRoute,
      featureFlags: {
        enableColumnShortForm: this.props.configJSON?.enableColumnShortForm
      }
    });
    this.gridApi = {};
    this.state = {
      quickFilterText: '',
      headerFilter: PAST_PICKUP_GRID_FILTER_LIST[0],
      panelFilter: PICKUP_STATUS.unassigned,
      selectedPickupData: null
    };
    this.priorityShipmentList = this.props.DispatchStore.priorityAllShipmentList;
    this.gridContext = {
      pickupTypeOptions: getPickupTypes(this.props.DispatchStore.pickupPriorityShipmentList),
      specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.priorityShipmentList),
      terminalOffset: this.props.DispatchStore.terminalOffset
    };
  }

  onSearchTextChanged = (searchText) => {
    this.setState({
      quickFilterText: searchText
    });
  };

  onGridReady = ({ api }) => {
    this.gridApi = api;
  };

  constructStatusBar = (filteredData) => {
    return [
      {
        label: filteredData.length + ' Pickups',
        filterCondition: PICKUP_STATUS.allPickups
      }
    ];
  };

  panelFilterChanged = (filter) => {
    this.setState(
      {
        panelFilter: filter
      },
      () => this.gridApi.onFilterChanged?.()
    );
  };

  doesExternalFilterPass = (node) => {
    let doesFilterPass;
    doesFilterPass = filterPickup({
      pickup: node.data,
      pickupFilter: this.state.headerFilter
    });

    return doesFilterPass;
  };

  exportCSV = () => {
    if (!this.gridApi) {
      return;
    }
    const columnIds = this.gridApi.getColumns()
      .reduce((columnKeys, column) => {
        if (column.getColDef().headerName) {
          columnKeys.push(column.getColId());
        }
        return columnKeys;
      }, []);
    const params = {
      fileName: 'PastPickup',
      columnKeys: columnIds,
      processCellCallback: (cell) => csvFormulaInjectionSanitizer(cell.value)
    };
    this.gridApi.exportDataAsCsv(params);
  };

  onFilterChange = (option) => {
    this.setState({ headerFilter: option }, () => this.gridApi.onFilterChanged?.());
  };

  onSelectionChanged = (_event) => {
    if (!this.gridApi) {
      return;
    }
    const selectedPickup = this.gridApi.getSelectedRows()?.[0] ?? null;
    this.setState({
      selectedPickupData: selectedPickup
        ? { ...selectedPickup, stopType: appConstants.STOP_TYPE.Pickup }
        : null
    });
  };

  emitShowPickupDetails = (isPastPickup) => () => {
    const { selectedPickupData: pastPickupDetails } = this.state;
    this.props.glEventHub.emit(
      'showPickupDetails',
      pastPickupDetails.pickupId,
      isPastPickup,
      pastPickupDetails,
    );

    // Calling api here to retrieve `proIds` from this response, which are required to prepare `driverDetails` for stop images. Most fields needed for `driverDetails` are already available in `selectedPickupData`, and `proIds` can be obtained using the existing `pastShipments` APIs.
    getPickupShipmentList({
      terminalCode: this.props.userData.userProfile.terminal.code,
      shipmentInfo: {gridStatus: appConstants.GRID_STATUS.LOADING},
      pickupNumber: pastPickupDetails.pickupNumber,
      isPastPickup: true, //preload past shipments data irrespective of tab selected, as fields in shipment data are needed for stop images
      shipmentAPI: 'GetPastPickupShipments'
    }).then(({data: pastPickupShipments}) => {
      const pastDriverDetails = getDriverStopDetailsForPastPickup({
        pastPickupDetails,
        pastPickupShipments,        
      });
       /* pastDriverDetails: Used to display stop images for past pickups since driver details from past pickups are not available in the store.
      pastPickupShipments: As this response is already aval, we can use it to avoid extra api call while switching 'Shipment Details'/'Pickup History' Tab */
      this.props.router.updateNavigationData({driverDetails: pastDriverDetails, pastPickupShipments,shipmentGridStatus: appConstants.GRID_STATUS.SUCCESS});
    });
  };

  render() {
    const { pickups, gridMessage, changeSelectedDateRange, selectedDateRange } = this.props;
    const { selectedPickupData } = this.state;
    return (
      <React.Fragment>
        <GridHeader
          gridApi={this.gridApi}
          updateSearchText={this.onSearchTextChanged}
          exportCSV={this.exportCSV}
          onFilterChange={this.onFilterChange}
          onViewHistoryClick={this.emitShowPickupDetails(true)}
          selectedPickupData={selectedPickupData}
          selectedDateRange={selectedDateRange}
          onDateRangeChange={changeSelectedDateRange}
        />
        <DataGrid
          defaultColDef={this.gridConfig.config.defaultColDef}
          columnDefs={this.gridConfig.config.columns}
          columnTypes={this.gridConfig.config.columnTypes}
          config={this.gridConfig.config}
          rowSelection={this.gridConfig.config.rowSelection}
          rowDeselection={this.gridConfig.config.rowDeselection}
          rowData={pickups}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.onSelectionChanged}
          onCellDoubleClicked={this.emitShowPickupDetails(false)}
          panelLabel={(filteredData) => (
            <StatusBar
              data={this.constructStatusBar(
                filteredData.filter?.((data) => this.doesExternalFilterPass({ data })) ?? []
              )}
              filterGridData={this.panelFilterChanged}
            />
          )}
          isExternalFilterPresent={() =>
            Boolean(this.state.headerFilter.value || this.state.panelFilter)
          }
          doesExternalFilterPass={this.doesExternalFilterPass}
          quickFilterText={this.state.quickFilterText}
          deltaRowDataMode={true}
          onRowDataUpdated={this.onRowDataUpdated}
          getRowId={(params) => params.data.pickupId}
          showErrorMessage={gridMessage}
          context={this.gridContext}
        />
      </React.Fragment>
    );
  }
}
export default compose(withUserProfile, withAppConfig, withRouter)(PickupsGrid);
